import React, { useState, useEffect, useRef, useCallback } from "react";
import clsx from "clsx";
import { getTextWidth } from "../../../Utils/Utils";
import FilterSelectListTypes from "./FilterSelectListTypes";
import {
  LogicalOperatorsOptions,
  SupportedTypes,
  LogicalOperatorEnumerator,
} from "./LogicalOperatorsOptions";
import LogicalOperatorBooleanView from "./LogicalOperatorBooleanView";
import { getFilterDescription } from "components/QueryBuilder/QueryFilters/QueryFilterCreator";
import { debounce } from "lodash";
import FilterSelectTag from "./FilterSelectTag";

const getOptionResult = (option) => {
  const res = LogicalOperatorsOptions.find((d) => d.description === option);
  return res ? res.displayedValue : LogicalOperatorsOptions[0].displayedValue;
};

const FilterSelect = (props) => {
  const tabDefinitions = props.tabDefinitions || [
    {
      name: "metrics",
      displayedName: "METRICS",
    },
    {
      name: "dimension",
      displayedName: "DIMENSIONS",
      selected: true,
    },
  ];
  const {
    mode,
    data,
    loadingStatus,
    fieldType,
    tabNames = [],
    maxWidth = 360,
    placeholder,
    alwaysOpen,
    onChangeTabs,
    defaultTab,
    noDataMessage,
    isDataFetchingConcluded,
    dimensionType,
  } = props;

  const [tabs, setTabs] = useState(
    tabDefinitions
      .filter((tab) => tabNames.includes(tab.name))
      .map((tab) => ({
        ...tab,
        selected: tab.name === defaultTab || tab.selected,
      }))
  );
  // Defines the current primitive type which will be used as reference for the
  // <FilterSelectListTypes/> and also to filter all LogicalOperatorsOptions by the current primitive value
  const currentPrimitiveType =
    (props.defaultLogicalOperator &&
      props.defaultLogicalOperator.primitiveType) ||
    SupportedTypes.String;

  // filter all logical operator related to the selected primitive type
  const logicalOperatorsByType = LogicalOperatorsOptions.filter(
    (lo) =>
      lo.supportedTypes.filter(
        (st) => st.supportedType === currentPrimitiveType
      ).length
  );

  /**
   * Every time that `currentPrimitiveType` is changed, check if the defaultLogicalOperator is in the list
   * of the logicalOperatorsByType (currentPrimitiveType)... if it isn't, set the first one as default
   */
  useEffect(() => {
    // if the defaultLogicalOperator is NOT in the list logicalOperatorsByType?
    if (
      !["simple"].includes(mode) &&
      logicalOperatorsByType.length &&
      !logicalOperatorsByType.find(
        (lo) =>
          lo.description ===
          (props.defaultLogicalOperator &&
            props.defaultLogicalOperator.logicalOperator &&
            props.defaultLogicalOperator.logicalOperator.description)
      )
    )
      //set the first one as default
      onClickOptDrop(logicalOperatorsByType[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrimitiveType]);

  const isDimensionsItems = fieldType === "dimensions_items";

  const [isOpen, setIsOpen] = useState({
    dropDown: alwaysOpen ? true : false,
    option: false,
  });

  useEffect(() => {
    // trigger the parent function to let it know that the menu is open or closed
    props.onSetIsOpen && props.onSetIsOpen(isOpen.dropDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen.dropDown]);

  const [choosed, setChoosed] = useState({});

  /**
   * Handle filter removal
   */
  useEffect(() => {
    // if the dropdown is closed and there's a logical operator set
    if (!isOpen.dropDown && props.defaultLogicalOperator?.logicalOperator) {
      // check if the LO `value` is not set
      const value =
        props.defaultLogicalOperator.logicalOperator.value ||
        props.defaultLogicalOperator?.value;
      // now, we check if the LO `value` was removed in order to consider to remove the current filter
      // from the field list
      if (
        // Checks EQUALs LO
        (Array.isArray(value) && value.length === 0) ||
        // Checks CONTAINS LO
        value?.list?.length === 0
      )
        props.removeFilter(props.filterIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen.dropDown, props.defaultLogicalOperator?.logicalOperator]);

  useEffect(() => {
    let choosedValueStart = "";
    if (props.choosedValue && isDimensionsItems) {
      props.choosedValue.forEach(
        (el, i) =>
          (choosedValueStart += (i === 0 ? "" : ", ") + el.friendlyName)
      );
    }
    setChoosed({
      dimension: props.choosedDimension ? props.choosedDimension : false,
      value: choosedValueStart,
      option:
        (props.defaultLogicalOperator &&
          props.defaultLogicalOperator.logicalOperator &&
          props.defaultLogicalOperator.logicalOperator.description) ||
        LogicalOperatorsOptions[0].description,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.choosedValue]);

  useEffect(() => {
    setChoosed({
      [fieldType]: props.value ? props.value.friendlyName : "",
      option: LogicalOperatorsOptions[0].description,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const [valueInputSearch, setValueInputSearch] = useState("");
  const [checkboxList, setCheckboxList] = useState([]);

  useEffect(() => {
    // clears the stale data when the new loading processs is started
    if (props.loadingStatus === "loading" && checkboxList.length > 0) {
      setCheckboxList([]);
      props.setData && props.setData(null);
    }
  }, [props, checkboxList, setCheckboxList]);

  useEffect(() => {
    if (isDimensionsItems && data && data.length) {
      // concatenates the current data with the new one coming from re-fetched data
      const tempList = [...checkboxList];
      data.forEach((el) => {
        if (
          !el.dimensionId &&
          // because the BE returns REPEATED ids with different names, we need to find it
          // from the cache list by compounding the ID + name
          !tempList.find((cb) => cb.id === el.id + el.name)
        ) {
          tempList.push({
            ...el,
            // because the BE returns REPEATED ids with different names, we need to compound it to
            // make it unique
            id: el.id + el.name,
            checked:
              props.choosedValue.findIndex(
                (cV) => el.friendlyName === cV.friendlyName
              ) !== -1,
          });
        }
      });
      setCheckboxList(tempList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /**
   * Call the parent onChangeValue when the input search from the Equal List View is changed
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callOnChangeValueInput = useCallback(
    debounce((inputValue) => {
      props.onChangeValueInput && props.onChangeValueInput(inputValue);
    }, 500),
    [data]
  );

  /**
   * Listen to the valueInputSearch to request for more data when the current
   * data doesn't contain element with the valueInputSearch
   */
  useEffect(() => {
    if (
      // find all elements that may be related to the search input
      data.filter(
        (e) =>
          e.friendlyName.toLowerCase().indexOf(valueInputSearch.toLowerCase()) >
            -1 || valueInputSearch.includes(e.friendlyName)
      ).length === 0 ||
      valueInputSearch.length === 0
    ) {
      callOnChangeValueInput(valueInputSearch);
    }
  }, [valueInputSearch, callOnChangeValueInput, data]);

  const [valueInputSearchMetrics, setValueInputSearchMetrics] = useState("");
  const refDropdown = useRef(null);
  const refInput = useRef(null);

  const [inputWidth, setInputWidth] = useState([]);

  const refOptionDropdownBlock = useRef(null);

  const [accentuatedEl, setAccentuatedEl] = useState({
    name: "",
    id: 0,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fitWidth = useCallback((value) => {
    if (choosed.dimension && choosed.value) {
      setInputWidth(
        getTextWidth(
          choosed.dimension +
            getOptionResult(choosed.option) +
            `"${choosed.value}"`
        ) *
          1.3 +
          12
      );
    } else if (choosed.dimension) {
      setInputWidth(
        getTextWidth(
          choosed.dimension +
            getFilterDescription(props.defaultLogicalOperator.logicalOperator)
        ) *
          1.3 +
          7
      );
    } else if (choosed[fieldType]) {
      setInputWidth(getTextWidth(choosed[fieldType]) * 1.3 + 5);
    } else {
      setInputWidth(
        placeholder
          ? getTextWidth(placeholder) + 40
          : getTextWidth("Select") + 20
      );
    }
  });

  useEffect(() => fitWidth(), [fitWidth]);

  const offFocus = useCallback(
    (event) => {
      if (refDropdown.current && !refDropdown.current.contains(event.target)) {
        if (isOpen.dropDown) {
          setIsOpen({
            ...isOpen,
            dropDown: false,
          });
        }
        setAccentuatedEl({
          name: "",
          id: 0,
        });
      }
    },
    [isOpen]
  );

  const refAccentuatedEl = useRef(null);

  const keydown = useCallback(
    (e) => {
      if (isOpen.dropDown) {
        if (e.code === "ArrowUp") {
          setAccentuatedEl(
            accentuatedEl.name
              ? {
                  name:
                    accentuatedEl.id === 0
                      ? data[data.length - 1].friendlyName
                      : data[accentuatedEl.id - 1].friendlyName,
                  id:
                    accentuatedEl.id === 0
                      ? data.length - 1
                      : accentuatedEl.id - 1,
                }
              : {
                  name: data[0].friendlyName,
                  id: 0,
                }
          );
        } else if (e.code === "ArrowDown") {
          setAccentuatedEl(
            accentuatedEl.name
              ? {
                  name:
                    accentuatedEl.id === data.length - 1
                      ? data[0].friendlyName
                      : data[accentuatedEl.id + 1].friendlyName,
                  id:
                    accentuatedEl.id === data.length - 1
                      ? 0
                      : accentuatedEl.id + 1,
                }
              : {
                  name: data[0].friendlyName,
                  id: 0,
                }
          );
        }
      }

      if (e.keyCode === 27) {
        setIsOpen({
          ...isOpen,
          dropDown: false,
        });
      }
    },
    [accentuatedEl, data, isOpen]
  );

  useEffect(() => {
    window.addEventListener("mousedown", offFocus, false);
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("mousedown", offFocus, false);
      window.removeEventListener("keydown", keydown);
    };
  }, [offFocus, keydown]);

  const pressEnter = (e, func, arg) => {
    if (e.key === "Enter") {
      func(...arg);
      setAccentuatedEl({
        name: "",
        id: 0,
      });
    }
  };

  const onStateChange = (selectedOption) => {
    // call the parent sending info regarding which data should be added as value in
    // the field and what kind of LO and PT is this
    props.onChangeParent &&
      props.onChangeParent(props.index, selectedOption, {
        ...props,
        logicalOperator:
          // set the logical operator that comes from parent props OR... set the first one as default
          (props.defaultLogicalOperator &&
            props.defaultLogicalOperator.logicalOperator) ||
          logicalOperatorsByType[0],
        primitiveType: currentPrimitiveType,
      });
  };

  const click = (e, name, type, selectedOption) => {
    e.stopPropagation();
    setChoosed({
      ...choosed,
      [type]: name,
    });

    if (["simple"].includes(mode)) {
      setValueInputSearchMetrics("");
      setIsOpen({
        dropDown: alwaysOpen ? true : false,
        option: false,
      });
      fitWidth(selectedOption);
      setAccentuatedEl({
        name: "",
        id: 0,
      });
    }

    if (selectedOption) {
      fitWidth(selectedOption);
      onStateChange(selectedOption);
    }
  };

  const onChangeInputSearch = (event) =>
    setValueInputSearch(event.target.value);

  const onChangeInputSearchMetrics = (event) => {
    setValueInputSearchMetrics(event.target.value);
    // when the user start to type... and in the case that the dropdown is not
    // opened yet... set focus
    !isOpen.dropDown &&
      setIsOpen({
        ...isOpen,
        dropDown: true,
      });
  };

  const clean = (e) => {
    e.stopPropagation();
    setChoosed({
      option: LogicalOperatorsOptions[0].description,
      value: false,
      dimension: false,
    });
    setCheckboxList([]);
    if (refInput.current) {
      refInput.current.value = "";
    }
    setInputWidth(0);
    if (props.cleanFilter && isDimensionsItems) props.cleanFilter(props.index);

    setAccentuatedEl({
      name: "",
      id: 0,
    });

    setValueInputSearch("");
    setValueInputSearchMetrics("");
    props.onClearInputSearch && props.onClearInputSearch();
  };

  /**
   * Automatically select checkboxes with pasted values that are separated by new lines ex.
   *   10:00 2023-01-20
   *   10:00 2023-01-21
   * @param {*} pastedValue
   */
  const handlePastedValue = (pastedValue) => {
    const possibleValues = pastedValue.split(/\r?\n/).map((e) => e.trim());

    const possibleIds = checkboxList
      .filter((e) => possibleValues.includes(e.friendlyName))
      .map((e) => e.id);
    handleInputChange(null, possibleIds);

    setValueInputSearch(pastedValue);
  };

  const handleInputChange = (ind, indList = []) => {
    let selectedOption = [];
    let newCL = checkboxList.map((el, i) =>
      i === ind || indList.includes(el.id)
        ? { ...el, checked: !el.checked }
        : { ...el }
    );

    let newValue = "";
    // define a junction string to probably be loaded after (all the checked elements)
    newCL.forEach((d) => {
      if (d.checked) {
        newValue += (newValue === "" ? "" : ", ") + '"' + d.friendlyName + '"';
      }
    });

    // set the choosed elements
    setChoosed({ ...choosed, value: newValue });
    // check the new checkbox list with the checked valued
    setCheckboxList([...newCL]);
    // get the selected options to be sent to the parent
    newCL.forEach((d) => d.checked && selectedOption.push(d));

    // send the selected options to the parent
    onStateChange(selectedOption);

    setAccentuatedEl({
      name: "",
      id: 0,
    });

    // if selectedOption is empty.. it means that all elements selected were removed
    if (!isOpen.dropDown && selectedOption.length === 0) {
      // remove the current filter
      props.removeFilter(props.filterIndex);
    }

    // if no value was selected or everything else was removed
    if (newValue.length === 0) {
      // clears the stale data
      setCheckboxList([]);
      props.setData && props.setData(null);
    }
  };

  const errorTpl = (
    <div className="p-2 text-center" style={{ fontSize: 16 }}>
      <span className="badge badge-danger">
        Error &rarr;{" "}
        <span
          onClick={() => {
            window.history.pushState(
              "",
              document.title,
              window.location.pathname + window.location.search
            );
            window.location.reload();
          }}
          className="query-inline-error"
        >
          Click to Refresh
        </span>
      </span>
    </div>
  );

  const onClickOptDrop = (el) => {
    setIsOpen({ dropDown: true, option: false });
    setChoosed({ ...choosed, option: el.description });

    checkboxList.length &&
      setCheckboxList(checkboxList.map((e) => ({ ...e, checked: false })));
    // trigger the parent callback sending the Logical Operator selected and the index of the filters
    props.onLogicalOperatorChange &&
      // set the logical operator to be handled by the parent.. sending the info
      // just of the friendlyName.. we don't need to send the whole object
      props.onLogicalOperatorChange(props.index, {
        description: el.description,
        logicalType: el.logicalType,
      });

    // because Has Value and Does Not have Value will not be triggered by the ADD FILTER button...
    if (
      [
        LogicalOperatorEnumerator.DoesNotHaveValue,
        LogicalOperatorEnumerator.HasValue,
      ].includes(el.description)
    ) {
      onApplyFilter({}, false);
    }
  };

  const OptionDropDown =
    logicalOperatorsByType.length === 0 ? null : (
      <div
        className="pb-1 pl-2 mt-1 option-dropdown-block"
        ref={refOptionDropdownBlock}
      >
        <span
          className="option-dropdown-header"
          onClick={() => setIsOpen({ ...isOpen, option: !isOpen.option })}
        >
          {choosed.option}
        </span>
        <ul className="option-dropdown">
          {isOpen.option
            ? logicalOperatorsByType.map((lo, ind) => (
                <li
                  key={ind}
                  onClick={() => onClickOptDrop(lo)}
                  className="dropdown-menu-item"
                >
                  {lo.description}
                </li>
              ))
            : ""}
        </ul>
      </div>
    );

  const selectedTab = tabs.find((tab) => tab.selected);

  const onClickTabs = (type) => {
    setTabs(tabs.map((d, j) => ({ ...d, selected: d.name === type })));
  };
  /**
   * After the user select a tab, we call the parent callback passing as reference the selected tab
   */
  useEffect(() => {
    const selectedTab = tabs.find((tab) => tab.selected);
    selectedTab && onChangeTabs && onChangeTabs(selectedTab.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  /**
   * When the use selects a primitive type for the logical operator,
   * we call the parent in order to let it set the type selected in the
   * filter according the current position in the array (based in the index)
   */
  const onSelectPrimitiveType = (type) => {
    props.onSelectPrimitiveType &&
      props.onSelectPrimitiveType(props.index, type);
  };

  /**
   * Returns the current Filter View which will apply all the parameters for filters based on the seleted Logical Operator X Primitive Type
   */
  const getCurrentFilterView = (props) => {
    let View = null;
    // get the current logical operator to find what's the view to shown based on the current primitive type
    const currentLogicalOperator = logicalOperatorsByType.find(
      (lo) => lo.description === choosed.option
    );

    // if no curent logical operator was applied BUT primitive value was (Boolean is an example...)
    if (!currentLogicalOperator) {
      // exclusively for Boolean, which isn't related to any Logical Operator
      if (currentPrimitiveType === SupportedTypes.Boolean)
        View = LogicalOperatorBooleanView;
      if (!View) return null;
    }

    // return the view filtered by the current primitive type applied
    View = View
      ? View
      : currentLogicalOperator.supportedTypes.find(
          (st) => st.supportedType === currentPrimitiveType
        ).view;

    // INFO: Currently these components are ReactClass components rather than functional components. WHY? Because I was
    // facing exceptions regarding: "rendered more hooks than previous render". It's probably caused by these dynamic func components
    // loaded creating new hooks after the lifecycle get concluded.
    return <View {...props} />;
  };

  /**
   * Method triggerd when the user clicks on the ADD FILTER button implemented inside
   * each LogicalOperatorView and being currently displayed
   */
  const onApplyFilter = (value, shouldClose = true) => {
    setChoosed({ ...choosed, value: value });
    props.onApplyFilter &&
      props.onApplyFilter(props.index, value, currentPrimitiveType);

    // by default, it should always close the dropdown
    if (shouldClose) {
      setIsOpen({
        ...isOpen,
        dropDown: false,
      });
    }
  };

  // prepare the props to be applied into the getCurrentFilterView
  const propsForFilterView =
    // specifically for [Equals, Doesn't Equal] && String , return this object because there are data coming from parent (the list)
    ~[
      LogicalOperatorEnumerator.Equals,
      LogicalOperatorEnumerator.DoesNotEqual,
    ].indexOf(choosed.option) && currentPrimitiveType === SupportedTypes.String
      ? {
          checkboxList,
          data,
          handleInputChange,
          handlePastedValue,
          valueInputSearch,
          onChangeInputSearch,
          isOpen,
          setValueInputSearch,
          loadingStatus,
        }
      : // for for other ways, return the props based on the filter (from the filter list -> metrics -> parent)
        {
          // method to let the parent know which value should be applied
          onApplyFilter,
          // which logical operator is selected
          choosed,
          // what are the detailf for the logical operator (like data and for what LO is this data for - the user could be migrating from a LO to another)
          logicalOperator: {
            ...(props.defaultLogicalOperator &&
              props.defaultLogicalOperator.logicalOperator),
          },
          // what's the current primitive type to be compared with the LO and its data
          primitiveType: currentPrimitiveType,
        };

  const inputValue = ["simple"].includes(mode)
    ? !isOpen.dropDown && choosed[fieldType]
      ? choosed[fieldType]
      : valueInputSearchMetrics //(valueInputSearchMetrics ? valueInputSearchMetrics : (choosed[fieldType] ? choosed[fieldType] : ""))
    : isOpen.dropDown && choosed.dimension
    ? choosed.dimension
    : choosed.dimension && choosed.value
    ? choosed.dimension + getOptionResult(choosed.option) + `"${choosed.value}"`
    : choosed.dimension
    ? choosed.dimension +
      getFilterDescription(props.defaultLogicalOperator.logicalOperator)
    : valueInputSearchMetrics;

  const groupedData = React.useMemo(
    () =>
      data
        .filter((d) => d.node)
        .map((node) => ({
          ...node,
          children: data.filter(
            (e) => e.category && e.category.friendlyName === node.friendlyName
          ),
        })),
    [data]
  );

  const showTags =
    props &&
    props.defaultLogicalOperator &&
    props.defaultLogicalOperator.logicalOperator &&
    [
      LogicalOperatorEnumerator.Equals,
      LogicalOperatorEnumerator.Contains,
    ].indexOf(props.defaultLogicalOperator.logicalOperator.description) >= 0 &&
    inputValue.length > 0 &&
    !isOpen.dropDown;

  const onInputClick = () => {
    setIsOpen({
      dropDown: true,
      option: false,
    });
  };

  /**
   * Removes an element.. or from the checklist.. or from the list
   * @param {*} event
   * @param {*} element
   */
  const removeTag = (event, element) => {
    event.stopPropagation();
    // in the case that the list comes from Contains...
    if (
      props.defaultLogicalOperator &&
      props.defaultLogicalOperator.logicalOperator &&
      props.defaultLogicalOperator.logicalOperator.value &&
      props.defaultLogicalOperator.logicalOperator.value.list
    ) {
      // removes the element from the list
      const value = props.defaultLogicalOperator.logicalOperator.value;
      value.list = value.list.filter((item) => item !== element);
      // re-apply the value
      onApplyFilter(value);
    } else {
      // in the case it's a checklist.. just call the method which removes a value
      handleInputChange(element);
    }
  };

  // if it's in display mode.. show tags when the rules applies
  return showTags ? (
    <div className="filter-select-input-control">
      <div className="filter-select-input-container" onClick={onInputClick}>
        <div className="filter-select-input-multivalue">
          <div className="filter-select-input-tag-description">
            {props.choosedDimension}&nbsp;
            {(
              props.defaultLogicalOperator.logicalOperator.displayedValue ||
              props.defaultLogicalOperator.logicalOperator.description
            )
              .trim()
              .toLowerCase()}
          </div>
        </div>
        {checkboxList
          .filter((e) => e.checked)
          .map((e) => (
            <FilterSelectTag
              key={e.id}
              label={e.friendlyName}
              onClick={(event) =>
                removeTag(
                  event,
                  checkboxList.findIndex((c) => c.id === e.id)
                )
              }
            />
          ))}
        {props.defaultLogicalOperator &&
          props.defaultLogicalOperator.logicalOperator &&
          props.defaultLogicalOperator.logicalOperator.value &&
          props.defaultLogicalOperator.logicalOperator.value.list &&
          props.defaultLogicalOperator.logicalOperator.value.list.map(
            (e, index) => (
              <FilterSelectTag
                key={index}
                label={e}
                onClick={(event) => removeTag(event, e)}
              />
            )
          )}
      </div>
    </div>
  ) : (
    //   display the filter input
    <span
      ref={refDropdown}
      className="filter-select"
      id={props.fieldType && props.index ? props.fieldType + props.index : ""}
    >
      <div className="filter-select-input">{inputValue}</div>
      <input
        id={props.fieldType && props.index ? props.fieldType + props.index : ""}
        autoFocus={!props.value || isOpen.dropDown}
        type="text"
        className={
          isOpen.dropDown && !choosed.dimension
            ? "input-active input input-empty"
            : isOpen.dropDown
            ? "input-active input"
            : "input"
        }
        ref={refInput}
        value={inputValue}
        placeholder={
          isOpen.dropDown ? "Search for" : placeholder ? placeholder : "Select"
        }
        onChange={onChangeInputSearchMetrics}
        readOnly={!["simple"].includes(mode) && choosed.dimension ? true : ""}
        style={{
          width: isOpen.dropDown
            ? maxWidth - 10 + "px"
            : inputWidth > 0
            ? inputWidth + "px"
            : "100px",
          maxWidth: "820px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...(showTags && {
            display: "flex",
            padding: "0",
            maxHeight: "31px",
            height: "31px",
            marginTop: "1px",
          }),
        }}
        onClick={onInputClick}
      />

      {isOpen.dropDown && isDimensionsItems ? (
        <span onClick={clean} className="clean-button">
          Clear
        </span>
      ) : (
        ""
      )}

      {isOpen.dropDown && !isDimensionsItems && (
        <div
          className="filter-menu"
          style={{
            width: isOpen.dropDown
              ? maxWidth + "px"
              : refInput.current
              ? refInput.current.offsetWidth + 10
              : "",
          }}
        >
          {tabs.length ? (
            <div className="pt-1 pb-2 pl-2 text-left filter-menu-tabs">
              {tabs.map((tab) => (
                <span
                  key={"tab_" + tab.name}
                  className={clsx("md-buttons", {
                    "md-buttons--active": tab.selected,
                  })}
                  onClick={() => onClickTabs(tab.name)}
                >
                  {tab.displayedName}
                </span>
              ))}
            </div>
          ) : null}

          <ul>
            {isDataFetchingConcluded && data.length === 0 && (
              <li className="filter-menu-item">
                {noDataMessage && noDataMessage}
              </li>
            )}
            {loadingStatus === "loading" ? (
              <div className="pt-2">{loadingTpl}</div>
            ) : loadingStatus === "error" ? (
              <div className="">{errorTpl}</div>
            ) : selectedTab && selectedTab.name === "metrics" ? (
              data.map((el, ind) => {
                if (
                  !valueInputSearchMetrics ||
                  el.friendlyName
                    .toLowerCase()
                    .indexOf(valueInputSearchMetrics.toLowerCase()) !== -1
                )
                  return (
                    <span key={ind}>
                      <input
                        className="filter-menu-item-input"
                        ref={accentuatedEl.id === ind ? refAccentuatedEl : null}
                        onKeyPress={(e) =>
                          pressEnter(e, click, [
                            e,
                            el.friendlyName,
                            fieldType,
                            el,
                          ])
                        }
                      />
                      <li
                        className="filter-menu-item"
                        onClick={(e) =>
                          click(e, el.friendlyName, fieldType, el)
                        }
                        onMouseEnter={() =>
                          setAccentuatedEl({
                            name: el.friendlyName,
                            id: ind,
                          })
                        }
                      >
                        {el.friendlyName}
                      </li>
                    </span>
                  );
                else return "";
              })
            ) : (
              data.map((el, ind) => {
                if (
                  !valueInputSearchMetrics ||
                  el.friendlyName
                    .toLowerCase()
                    .indexOf(valueInputSearchMetrics.toLowerCase()) !== -1 ||
                  // if the current element is a node and it does contain the element that we're searching for...
                  // show it as a category
                  (el.node &&
                    groupedData.filter(
                      (g) =>
                        el.friendlyName === g.friendlyName &&
                        g.children.filter(
                          (c) =>
                            c.friendlyName
                              .toLowerCase()
                              .indexOf(valueInputSearchMetrics.toLowerCase()) >
                            -1
                        ).length > 0
                    ).length > 0)
                )
                  return (
                    <span className="d-flex filter-menu-tabs" key={ind}>
                      {!el.node && (
                        <input
                          className="filter-menu-item-input"
                          ref={
                            accentuatedEl.id === ind ? refAccentuatedEl : null
                          }
                          onKeyPress={(e) =>
                            pressEnter(e, click, [
                              e,
                              el.friendlyName,
                              fieldType,
                              el,
                            ])
                          }
                        />
                      )}
                      {el.node ? (
                        <li key={el.id} className={"filter-menu-item-node"}>
                          {el.friendlyName}
                        </li>
                      ) : (
                        <li
                          key={el.id}
                          className={
                            ["simple"].includes(mode)
                              ? "filter-menu-item"
                              : "filter-menu-item filter-menu-item-arrow"
                          }
                          onClick={(e) =>
                            click(e, el.friendlyName, fieldType, el)
                          }
                          onMouseEnter={() =>
                            setAccentuatedEl({
                              name: el.friendlyName,
                              id: ind,
                            })
                          }
                        >
                          {el.friendlyName}
                        </li>
                      )}
                    </span>
                  );
                else return "";
              })
            )}
          </ul>
        </div>
      )}
      {isOpen.dropDown && isDimensionsItems && (
        <div
          className="filter-menu"
          style={{
            width: isOpen.dropDown
              ? maxWidth + "px"
              : refInput.current
              ? refInput.current.offsetWidth + 10
              : "",
          }}
        >
          <div>
            {OptionDropDown}
            <FilterSelectListTypes
              onOptionClick={onSelectPrimitiveType}
              value={currentPrimitiveType}
              type={dimensionType}
            />
          </div>
          <ul>
            {loadingStatus === "error" ? (
              <div className="">{errorTpl}</div>
            ) : (
              getCurrentFilterView(propsForFilterView)
            )}
          </ul>
        </div>
      )}
    </span>
  );
};

export const loadingTpl = (
  <div style={{ minHeight: 30 }} className="mt-4 ml-3 mr-3 text-center">
    <span>
      <div className="circle-scale circle-scale--sm d-inline-block" />
    </span>
  </div>
);

export default FilterSelect;
